class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-cart-overlay]');
    this.panel = this.querySelector('[data-cart-panel]');
    this.body = this.querySelector('[data-cart-body]');
    this.footer = this.querySelector('[data-cart-footer]');
    this.countEl = this.querySelector('[data-cart-count]');
    this.subtotalEl = this.querySelector('[data-cart-subtotal]');

    this._onOverlay = () => this.close();
    this._onKeydown = (e) => { if (e.key === 'Escape') this.close(); };
    this._onClick = (e) => this._handleClick(e);

    this.overlay?.addEventListener('click', this._onOverlay);
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onClick);

    const ul = window.ultimateLiving = window.ultimateLiving || {};
    ul.openCart = () => this.open();
    ul.closeCart = () => this.close();
    ul.renderCart = (cart) => this.renderCart(cart);
  }

  disconnectedCallback() {
    this.overlay?.removeEventListener('click', this._onOverlay);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onClick);
  }

  _handleClick(e) {
    if (e.target.closest('[data-cart-link]')) {
      e.preventDefault();
      this.open();
      return;
    }
    if (e.target.closest('[data-cart-close]')) {
      this.close();
      return;
    }
    const qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      if (qtyBtn.disabled) return;
      const wrap = qtyBtn.closest('[data-qty-wrap]');
      const numEl = wrap?.querySelector('[data-qty-num]');
      const current = numEl ? parseInt(numEl.textContent, 10) : 1;
      // Floor at 1, not 0: _changeQty treats 0 as a removal, so stepping
      // down from 1 would silently delete the line. Removing stays an
      // explicit action through the Remove button.
      const next = Math.max(1, current + parseInt(qtyBtn.dataset.delta, 10));
      if (next === current) return;
      this._changeQty(qtyBtn.dataset.key, next);
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) this._changeQty(removeBtn.dataset.key, 0);
  }

  open() {
    const ul = window.ultimateLiving || {};
    const fetch_ = ul.fetchCart || (() =>
      fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(r => r.json())
    );
    fetch_().then(cart => {
      this.renderCart(cart);
      this.panel?.removeAttribute('hidden');
      this.overlay?.classList.add('is-open');
      this.panel?.classList.add('is-open');
      document.body.classList.add('cart-drawer-open');
    });
  }

  close() {
    this.panel?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.panel?.addEventListener('transitionend', () => {
      if (!this.panel.classList.contains('is-open')) this.panel.setAttribute('hidden', '');
    }, { once: true });
    document.body.classList.remove('cart-drawer-open');
  }

  _changeQty(key, qty) {
    const ul = window.ultimateLiving || {};
    const url = ul.routes?.cartChangeUrl || '/cart/change.js';
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(res => { if (!res.ok) throw new Error('Cart update failed'); return res.json(); })
      .then(cart => this.renderCart(cart));
  }

  renderCart(cart) {
    const ul = window.ultimateLiving || {};
    const count = ul.logicalCartCount ? ul.logicalCartCount(cart) : (cart.items || []).length;
    if (ul.updateCartCount) ul.updateCartCount(count);
    if (this.countEl) this.countEl.textContent = String(count);

    if (!cart.items?.length) {
      if (this.body) this.body.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      this.footer?.setAttribute('hidden', '');
      return;
    }

    if (this.body) this.body.innerHTML = cart.items.map(item => this._renderItem(item)).join('');
    if (this.subtotalEl) this.subtotalEl.textContent = this._money(cart.total_price);
    this.footer?.removeAttribute('hidden');
  }

  _renderItem(item) {
    // Multi-tub lines are variants of a SEPARATE Shopify Bundles product
    // ("Green Miracle Powder Bundles"), whose own title carries no tub count,
    // so neither the real photo nor the quantity can come from /cart.js.
    // layout/theme.liquid publishes both per variant id; see the bundleLines
    // note there. Falls back to parsing the first number in variant_title,
    // the same signal the PDP widget matches on, for a line whose variant is
    // missing from the map (added via AJAX after page load).
    const bundleInfo = window.ultimateLiving?.bundleLines?.[item.variant_id] || null;
    const titleUnits = parseInt(((item.variant_title || '').match(/\d+/) || [])[0], 10) || 0;

    // A subscription line carries the MAIN product's variant, not a bundle
    // variant, so bundleLines never matches it: a "3 Tubs" plan was showing
    // the single-tub shot. layout/theme.liquid publishes the tubs-per-
    // shipment and matching photo per selling plan id (see the planLines
    // note there), resolved the way the PDP resolves it: the plan's real
    // "Product Quantity" option first, its name only as a last resort.
    const planId = item.selling_plan_allocation?.selling_plan?.id;
    const planInfo = planId ? (window.ultimateLiving?.planLines?.[planId] || null) : null;
    // Name fallback for a plan missing from the map, matching the PDP's
    // getPlanUnitsFromName. Anchored to "N tub(s)" so a cadence like
    // "3-Month Supply" is not misread as 3 tubs.
    const planNameUnits = (() => {
      const m = (item.selling_plan_allocation?.selling_plan?.name || '')
        .match(/(\d+)[\s-]*tubs?\b/i);
      return m ? parseInt(m[1], 10) : 0;
    })();

    const lineInfo = bundleInfo || planInfo;
    const bundleUnits = lineInfo?.units || planNameUnits || titleUnits;

    // /cart.js exposes the line's own image as item.image (variant image when
    // the variant has one, product image otherwise) and as featured_image.url;
    // both are checked since the two disagree on lines whose variant image was
    // set after the line was added. The former `_bundle_image` line property
    // this replaced was never written by any template in this theme (verified
    // repo-wide), so it always fell through to the generic shot.
    const cdnSrc = item.image || item.featured_image?.url || null;
    // Ask Shopify's CDN for the rendered size rather than dropping a
    // full-size photo into a 72px box. The published bundle image is already
    // requested at 144px, so this applies only to the line's own URL.
    const sizedCdn = cdnSrc ? cdnSrc.replace(/(\.(?:png|jpe?g|webp))(\?|$)/i, '_144x$1$2') : null;
    // Photo by tub count, so a 3-tub subscription and a 3-tub one-time
    // purchase always show the same grouped shot (tubImages in
    // layout/theme.liquid). Falls back to the line's own image for anything
    // that is not a known multi-tub tier.
    const tubShot = bundleUnits > 1
      ? (window.ultimateLiving?.tubImages?.[bundleUnits] || null)
      : null;
    const imgSrc = tubShot || sizedCdn;
    // Bundle shots are grouped tubs, so name the count for screen readers
    // instead of repeating the bare product title.
    const alt = bundleUnits > 1
      ? `${item.product_title}, ${bundleUnits} tubs`
      : item.product_title;
    const img = imgSrc
      ? `<img src="${imgSrc}" alt="${this._escape(alt)}" width="72" height="72" loading="lazy">`
      : '<div class="cart-drawer__img-placeholder"></div>';

    // Real plan names are inconsistent across this product's Recharge plans
    // ("3-Month Supply" vs "3-Tubs — Every 6 Months" vs "1-Tub — Every
    // Month" with no digit at all) — confirmed 2026-09-19 while auditing the
    // plan list for the frequency picker. Derives a clean, consistent
    // "Every N months" label from whatever month count is in the plan's own
    // name, defaulting to 1 when the name says "Every Month" with no digit,
    // rather than displaying the raw name verbatim.
    const cadenceLabel = (() => {
      const name = item.selling_plan_allocation?.selling_plan?.name || '';
      if (!name) return name;
      const match = name.match(/(\d+)[\s-]*months?/i);
      const months = match ? parseInt(match[1], 10) : (/every\s+month/i.test(name) ? 1 : null);
      if (months == null) return name;
      return months === 1 ? 'Every month' : `Every ${months} months`;
    })();
    // Subhead under the title. Subscriptions state their cadence; a one-time
    // bundle states its quantity, because the bundle product is titled
    // "Green Miracle Powder Bundles" with no count in it, so a 3-tub line
    // otherwise gives the customer no way to tell what they are buying.
    let plan = '';
    if (item.selling_plan_allocation) {
      // A multi-tub plan states its size too, for the same reason the
      // one-time bundle does: the product title carries no count, so
      // "Every 6 months" alone never says how many tubs ship.
      const sub = bundleUnits > 1
        ? `${bundleUnits} tubs &middot; ${this._escape(cadenceLabel)}`
        : this._escape(cadenceLabel);
      plan = `<span class="cart-drawer__plan">Subscribe &mdash; ${sub}</span>`;
    } else if (bundleUnits > 1) {
      plan = `<span class="cart-drawer__plan">${bundleUnits} tubs</span>`;
    }

    // Total tubs this line bills for, the basis of the savings figure below.
    //
    // A subscription is NOT bundleUnits * quantity: Recharge already encodes
    // the plan's size as the line quantity (a 3-tub plan sits in the cart as
    // quantity 3, see snippets/cart-item.liquid), so multiplying would count
    // the tubs twice and report an impossible saving (measured: a 3-tub plan
    // read as 9 tubs, "Save $563.94 (78%)"). The line quantity alone is the
    // real tub count there.
    //
    // A one-time bundle IS multiplied: quantity 2 of the "3 tubs" variant is
    // two separate bundles, so 6 tubs.
    const units = item.selling_plan_allocation
      ? item.quantity
      : (bundleUnits > 1 ? bundleUnits : 1) * item.quantity;

    // Compare against the single-tub variant's TRUE retail (compare_at_price,
    // $79.99) times the tub count, which is the basis every "Save" figure on
    // the Green Miracle page uses. original_line_price is deliberately not
    // the primary source: for a bundle line it is that bundle's own flat
    // total, so it misses the MSRP-vs-one-time gap entirely and would report
    // a smaller saving here than the cart page shows for the same line.
    const retailUnit = window.ultimateLiving?.retailByVariant?.[item.variant_id] || 0;
    const retailLine = retailUnit > 0 ? retailUnit * units : item.original_line_price;

    const saveCents = retailLine - item.line_price;
    const discounted = saveCents > 0 && retailLine > 0;
    const pct = discounted ? Math.round((saveCents / retailLine) * 100) : 0;
    const price = discounted
      ? `<s class="cart-drawer__price-was">${this._money(retailLine)}</s> <span class="cart-drawer__price">${this._money(item.line_price)}</span> <span class="cart-drawer__savings">Save ${this._money(saveCents)} (${pct}%)</span>`
      : `<span class="cart-drawer__price">${this._money(item.line_price)}</span>`;

    // Quantity control. Subscription lines never get a stepper: Recharge
    // encodes the plan's frequency AS the line quantity (3-month = qty 3,
    // see the note in snippets/cart-item.liquid), so changing it here would
    // desync the line from its plan and alter what actually ships. Those
    // show a readable locked state instead of nothing, matching the cart
    // page's "Set by plan" treatment.
    //
    // One-time lines, including multi-tub bundles, do get a stepper. On a
    // bundle, 2 means two of that bundle (2 x 3 tubs billed as two bundles,
    // which is NOT the cheaper 6-tub tier) — the per-line label states the
    // tub count so that reads honestly.
    let qty;
    if (item.selling_plan_allocation) {
      qty = `
      <div class="cart-drawer__qty-wrap cart-drawer__qty-wrap--locked">
        <span class="cart-drawer__qty-num">${item.quantity}</span>
        <span class="cart-drawer__qty-locked">Set by plan</span>
      </div>`;
    } else {
      // Floor the decrement at 1 rather than 0: _changeQty treats 0 as a
      // removal, so a stray click on "-" would silently delete the line.
      // Removal stays an explicit action via the Remove button.
      const dec = item.quantity > 1 ? 1 : 0;
      qty = `
      <div class="cart-drawer__qty-wrap" data-qty-wrap>
        <button class="cart-drawer__qty-btn" data-cart-qty data-key="${item.key}" data-delta="-1" aria-label="Decrease quantity"${dec ? '' : ' disabled'}>&#x2212;</button>
        <span class="cart-drawer__qty-num" data-qty-num>${item.quantity}</span>
        <button class="cart-drawer__qty-btn" data-cart-qty data-key="${item.key}" data-delta="1" aria-label="Increase quantity">&#x2b;</button>
      </div>`;
    }

    return `
      <div class="cart-drawer__item" data-key="${item.key}">
        <div class="cart-drawer__img">${img}</div>
        <div class="cart-drawer__info">
          <p class="cart-drawer__item-title">${this._escape(item.product_title)}</p>
          ${plan}
          <div class="cart-drawer__item-footer">${qty}<div class="cart-drawer__item-price">${price}</div></div>
          <button class="cart-drawer__remove" data-cart-remove data-key="${item.key}" aria-label="Remove ${this._escape(item.product_title)}">Remove</button>
        </div>
      </div>`;
  }

  // Titles land in HTML attributes here, so escape the full set rather than
  // just the double quote the inline .replace() calls handled.
  _escape(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  _money(cents) {
    const amount = (cents / 100).toFixed(2);
    const fmt = window.ultimateLiving?.moneyFormat || '{{amount}}';
    return fmt
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)));
  }
}

customElements.define('cart-drawer', CartDrawer);
